import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Normalize Pydantic 422 array-shaped error details into a flat string so any
// call site that does `toast.error(err.response.data.detail)` won't crash
// React with "Objects are not valid as a React child". Object-shaped details
// (e.g. {error: "version_mismatch", ...}) are left intact — callers depend on
// their structure.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
      err.response.data.detail = detail
        .map((d) => {
          const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
          const msg = d?.msg || "invalid";
          return loc ? `${loc}: ${msg}` : msg;
        })
        .join("; ");
    }
    return Promise.reject(err);
  }
);

export default api;

/**
 * Coerce any axios error / Pydantic 422 response into a safe string so it can
 * be passed to `toast.error()` without crashing React (objects/arrays as
 * children throw "Objects are not valid as a React child").
 */
export function formatApiError(err, fallback = "") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        const msg = d?.msg || "invalid";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return err?.message || fallback;
}

// Helpers
export const authApi = {
  me: () => api.get("/auth/me").then((r) => r.data),
  exchangeSession: (session_id) => api.post("/auth/session", { session_id }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
};

export const projectsApi = {
  list: () => api.get("/projects").then((r) => r.data),
  create: (data) => api.post("/projects", data).then((r) => r.data),
  get: (id) => api.get(`/projects/${id}`).then((r) => r.data),
  full: (id) => api.get(`/projects/${id}/full`).then((r) => r.data),
  update: (id, data) => api.patch(`/projects/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/projects/${id}`).then((r) => r.data),
  duplicate: (id, data) => api.post(`/projects/${id}/duplicate`, data).then((r) => r.data),
  seedRiverside: () => api.post("/seed/riverside").then((r) => r.data),
};

export const membersApi = {
  list: (projectId) => api.get(`/projects/${projectId}/members`).then((r) => r.data),
  invite: (projectId, data) => api.post(`/projects/${projectId}/members`, data).then((r) => r.data),
  update: (projectId, mid, data) =>
    api.patch(`/projects/${projectId}/members/${mid}`, data).then((r) => r.data),
  remove: (projectId, mid) =>
    api.delete(`/projects/${projectId}/members/${mid}`).then((r) => r.data),
};

export const roomsApi = {
  create: (projectId, data) => api.post(`/projects/${projectId}/rooms`, data).then((r) => r.data),
  update: (id, data) => api.patch(`/rooms/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/rooms/${id}`).then((r) => r.data),
};

export const crewApi = {
  create: (projectId, data) => api.post(`/projects/${projectId}/crew`, data).then((r) => r.data),
  update: (id, data) => api.patch(`/crew/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/crew/${id}`).then((r) => r.data),
};

export const tasksApi = {
  create: (projectId, data) => api.post(`/projects/${projectId}/tasks`, data).then((r) => r.data),
  update: (id, data, opts = {}) => {
    const headers = {};
    if (opts.expected_updated_at) headers["If-Match"] = opts.expected_updated_at;
    return api.patch(`/tasks/${id}`, data, { headers }).then((r) => r.data);
  },
  remove: (id, opts = {}) =>
    api.delete(`/tasks/${id}`, { params: opts.cascade === false ? { cascade: false } : undefined }).then((r) => r.data),
  restore: (id) => api.post(`/tasks/${id}/restore`).then((r) => r.data),
  duplicate: (id) => api.post(`/tasks/${id}/duplicate`).then((r) => r.data),
  materialize: (id) => api.post(`/tasks/${id}/materialize`).then((r) => r.data),
  rollover: (id) => api.post(`/tasks/${id}/rollover`).then((r) => r.data),
  group: (projectId, taskIds, opts = {}) =>
    api.post(`/projects/${projectId}/tasks/group`, {
      task_ids: taskIds,
      ...(opts.layout ? { layout: opts.layout } : {}),
    }).then((r) => r.data),
  relayout: (id, body = {}) =>
    api.post(`/tasks/${id}/relayout`, body).then((r) => r.data),
};

export const blocksApi = {
  create: (projectId, data) => api.post(`/projects/${projectId}/schedule-blocks`, data).then((r) => r.data),
  update: (id, data, opts = {}) => {
    const headers = {};
    if (opts.expected_updated_at) headers["If-Match"] = opts.expected_updated_at;
    return api.patch(`/schedule-blocks/${id}`, data, { headers }).then((r) => r.data);
  },
  remove: (id) => api.delete(`/schedule-blocks/${id}`).then((r) => r.data),
  bulkUpdate: (ids, updates) => api.patch("/schedule-blocks/bulk", { ids, updates }).then((r) => r.data),
};

export const assignmentsApi = {
  create: (blockId, data) => api.post(`/schedule-blocks/${blockId}/assignments`, data).then((r) => r.data),
  createForTask: (taskId, data) => api.post(`/tasks/${taskId}/assignments`, data).then((r) => r.data),
  remove: (id) => api.delete(`/assignments/${id}`).then((r) => r.data),
};

export const blockersApi = {
  create: (taskId, data) => api.post(`/tasks/${taskId}/blockers`, data).then((r) => r.data),
  update: (id, data) => api.patch(`/blockers/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/blockers/${id}`).then((r) => r.data),
};

export const eventsApi = {
  forTask: (taskId, limit = 50) => api.get(`/schedule-events?task_id=${taskId}&limit=${limit}`).then((r) => r.data),
  forBlock: (blockId, limit = 50) => api.get(`/schedule-events?schedule_block_id=${blockId}&limit=${limit}`).then((r) => r.data),
  forProject: (projectId, limit = 200) =>
    api.get(`/schedule-events?project_id=${projectId}&limit=${limit}`).then((r) => r.data),
};

export const dailyNotesApi = {
  list: (projectId) => api.get(`/projects/${projectId}/daily-notes`).then((r) => r.data),
  lock: (projectId, payload) =>
    api.post(`/projects/${projectId}/daily-notes/lock`, payload).then((r) => r.data),
};

export const exportApi = {
  fetch: (projectId) => api.get(`/projects/${projectId}/export`).then((r) => r.data),
};

export const quotaApi = {
  get: () => api.get("/ai/quota").then((r) => r.data),
};

export const recurrenceApi = {
  info: (taskId) => api.get(`/tasks/${taskId}/recurrence-info`).then((r) => r.data),
};

export const equipmentApi = {
  create: (projectId, data) => api.post(`/projects/${projectId}/equipment`, data).then((r) => r.data),
  update: (id, data) => api.patch(`/equipment/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/equipment/${id}`).then((r) => r.data),
  // Allocations: per-room qty for a piece of equipment. Sending qty=0 removes
  // the allocation (the backend interprets that as a delete).
  upsertAllocation: (equipmentId, roomId, qty) =>
    api.post(`/equipment/${equipmentId}/allocations`, { room_id: roomId, qty }).then((r) => r.data),
  updateAllocation: (allocId, data) =>
    api.patch(`/equipment-allocations/${allocId}`, data).then((r) => r.data),
  removeAllocation: (allocId) =>
    api.delete(`/equipment-allocations/${allocId}`).then((r) => r.data),
  // Categories: project-managed list. The frontend syncs from /full but uses
  // these endpoints for mutation.
  listCategories: (projectId) =>
    api.get(`/projects/${projectId}/equipment-categories`).then((r) => r.data),
  createCategory: (projectId, name) =>
    api.post(`/projects/${projectId}/equipment-categories`, { name }).then((r) => r.data),
  renameCategory: (catId, name) =>
    api.patch(`/equipment-categories/${catId}`, { name }).then((r) => r.data),
  removeCategory: (catId, clearFromEquipment = true) =>
    api.delete(`/equipment-categories/${catId}?clear_from_equipment=${clearFromEquipment ? "true" : "false"}`).then((r) => r.data),
};

export const filesApi = {
  listForTask: (taskId) => api.get(`/tasks/${taskId}/files`).then((r) => r.data),
  upload: (taskId, file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    if (kind) fd.append("kind", kind);
    return api.post(`/tasks/${taskId}/files`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  remove: (fileId) => api.delete(`/files/${fileId}`).then((r) => r.data),
  downloadUrl: (fileId) => `${API_BASE}/files/${fileId}`,
};

export const aiApi = {
  command: (projectId, command) =>
    api.post("/ai/command", { project_id: projectId, command }).then((r) => r.data),
  estimate: ({ projectId, title, roomId, complexity, crewSize }) =>
    api.post("/ai/estimate", {
      project_id: projectId,
      title,
      room_id: roomId || null,
      complexity: complexity ?? null,
      crew_size: crewSize ?? null,
    }).then((r) => r.data),
  suggestSubtasks: ({ projectId, title, roomId, parentDurationMin }) =>
    api.post("/ai/suggest-subtasks", {
      project_id: projectId,
      title,
      room_id: roomId || null,
      parent_duration_min: parentDurationMin ?? null,
    }).then((r) => r.data),
  dailyWrap: (projectId, dateKey) =>
    api.post(`/projects/${projectId}/ai/daily-wrap`, { date_key: dateKey }).then((r) => r.data),
};

export const dayRolloverApi = {
  candidates: (projectId, today) =>
    api.get(`/projects/${projectId}/day-rollover`, { params: { today } }).then((r) => r.data),
  apply: (projectId, today, actions) =>
    api.post(`/projects/${projectId}/day-rollover/apply`, { today, actions }).then((r) => r.data),
};
